<template>
  <div class="c-calendar-components">

    <GotoMonth :available-months="availableMonths"/>

    <DatePicker :start-date="firstInstance" :end-date="lastInstance"/>

    <div class="c-loader" v-if="isLoading">
      <i class="fas fa-circle-notch fa-spin" aria-hidden="true"></i> Loading...
    </div>

    <FullCalendar ref="fullCalendar" :options="calendarOptions" />
  </div>
</template>

<script>
import FullCalendar from "@fullcalendar/vue"
import momentPlugin from "@fullcalendar/moment"
import moment from "moment"
import listPlugin from "@fullcalendar/list"
import DatePicker from "./DatePicker"
import { EventBus } from "../event-bus"
import GotoMonth from "./GotoMonth"
import axios from "axios"

export default {
  name: "Calendar",
  props: ["firstInstance", "lastInstance", "availableMonths"],
  components: {
    FullCalendar,
    GotoMonth,
    DatePicker,
  },
  data() {
    return {
      calendarOptions: {
        firstDay: 1,
        headerToolbar: {
          start: "title",
          end: "",
        },
        dayMaxEventRows: true,
        views: {},
        footerToolbar: false,
        height: "auto",
        plugins: [listPlugin, momentPlugin],
        initialView: "listDay",
        events: [],
        displayEventTime: false,
        eventContent: this.eventContent,
        listDayFormat: "dddd, MMMM DD",
        listDaySideFormat: false,
        fixedWeekCount: false,
        timeZone: "UTC",
        initialDate: this.firstInstance,
        validRange: this.validRange,
        titleFormat: "ddd D MMMM",
      },
      isLoading: true,
      allInstances: {},
      calendarApi: null,
      currentView: "listDay",
    };
  },
  mounted() {
    this.addEventListeners();
    this.calendarApi = this.$refs.fullCalendar.getApi();
    const isJumpToMonth = this.urlParamJumpMonth();
    // This check avoids requesting for events twice on jump to month requests
    if (!isJumpToMonth) {
      this.fetchEvents();
    }
  },
  computed: {
    numberOfMonthsNeeded() {
      // Provides the number of months from today to the last instance
      return moment(this.lastInstance).diff(moment(), 'months')
    },
    lastDate() {
      return moment(this.lastInstance).toDate()
    }
  },
  created() {
    this.currentView = "listDay"
  },
  methods: {
    addEventListeners() {

      EventBus.$on("goToMonth", this.gotoMonth);
      EventBus.$on("dateSelected", this.goToSelectedDate)

    },
    goToSelectedDate(selectedDate) {
      const date = moment(selectedDate)
      this.calendarApi.gotoDate(date.format('YYYY-MM-DD'))
      this.fetchEvents()
    },
    eventContent(arg) {
      return {
        html: arg.event.title,
      };
    },
    fetchEvents() {
        let currentDate = moment.utc(this.calendarApi.currentData.currentDate),
            currentDateString = currentDate.format("YYYY-MM-DD");

        if (this.allInstances.hasOwnProperty(currentDateString)) {
            this.calendarOptions.events = this.allInstances[currentDateString]
        } else {

            this.isLoading = true;
            document.body.classList.add('state--calendar-loading')
            // Otherwise, fetch the instances
            let params = this.setupQueryParams();

            axios.get(window.wpAjax.url, {
                params: params,
            }).then((response) => {
                const instances = response.data;
                // Push our instances to the allInstances property, so we can use those next time rather than make another ajax call
                this.allInstances[currentDateString] = instances;

                // Assign the instances to our property as that is passed to the calendar
                this.calendarOptions.events = instances;
                this.isLoading = false;
                document.body.classList.remove('state--calendar-loading')
            }).catch((error) => {
                this.isLoading = false;
                document.body.classList.remove('state--calendar-loading')
                console.log(error);
            });
        }
    },
    gotoMonth(selectedMonth) {

      let month = moment(new Date(selectedMonth))
      const firstInstance = moment(this.firstInstance)
      if (month.utc().startOf("month") < firstInstance) {
        month = firstInstance
      } else {
        month = month.utc().startOf("month");
      }

      this.calendarApi.gotoDate(month.format("YYYY-MM-DD"));
      this.fetchEvents();
      this.updateMonth(month.format("YYYY-MM-DD"));
    },
    setupQueryParams() {
      return {
        action: "basethemeCalendarRequest",
        start: moment.utc(this.calendarApi.currentData.currentDate).format(),
        end: moment.utc(this.calendarApi.currentData.currentDate).endOf("day").format(),
      };
    },
    urlParamJumpMonth() {
      const urlParams = new URLSearchParams(window.location.search);

      if (urlParams.has("month")) {
        const selectedMonth = urlParams.get("month");
        this.gotoMonth(selectedMonth);
        return true;
      }

      return false;
    },
    updateMonth(month) {
      let monthString = moment(new Date(month)).utc().startOf("month").format("YYYY-MM");
      EventBus.$emit("updateDatePicker", month);

      let url = new URL(window.location);
      url.searchParams.set("month", monthString);
      window.history.replaceState({}, "", url);
    },
    validRange(nowDate) {
      if (this.firstInstance && this.lastInstance) {
        return {
          start: this.firstInstance,
          end: this.lastInstance
        }
      }
      return {
        start: nowDate
      }
    },
    on(selector, eventType, childSelector, eventHandler) {
      const elements = document.querySelectorAll(selector);
      for (let element of elements) {
        element.addEventListener(eventType, (eventOnElement) => {
          if (eventOnElement.target.matches(childSelector)) {
            eventHandler(eventOnElement);
          }
        });
      }
    },
  },
};
</script>

<style lang='css'>
@import "~@fullcalendar/common/main.css";
@import "~@fullcalendar/list/main.css";
</style>
